export function formatVnd(amount: number | null | undefined): string {
  if (amount == null || Number.isNaN(amount)) return "—";
  return `${amount.toLocaleString("vi-VN")}đ`;
}

// Compact VND for chart axes / tight cards: 1_200_000 -> "1,2tr", 45_000 -> "45k".
export function formatVndCompact(amount: number | null | undefined): string {
  if (amount == null || Number.isNaN(amount)) return "0";
  const abs = Math.abs(amount);
  if (abs >= 1_000_000_000)
    return `${(amount / 1_000_000_000).toLocaleString("vi-VN", { maximumFractionDigits: 1 })}tỷ`;
  if (abs >= 1_000_000)
    return `${(amount / 1_000_000).toLocaleString("vi-VN", { maximumFractionDigits: 1 })}tr`;
  if (abs >= 1_000)
    return `${Math.round(amount / 1_000).toLocaleString("vi-VN")}k`;
  return amount.toLocaleString("vi-VN");
}

// "2026-06-18" (VN day key from the analytics API) -> "18/06" for axis ticks.
export function formatDayShort(isoDay: string): string {
  const parts = isoDay.split("-");
  if (parts.length !== 3) return isoDay;
  return `${parts[2]}/${parts[1]}`;
}

const DAY_NAMES = [
  "Chủ nhật",
  "Thứ 2",
  "Thứ 3",
  "Thứ 4",
  "Thứ 5",
  "Thứ 6",
  "Thứ 7",
];

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

export function formatDateTimeVn(iso: string | Date): string {
  const d = typeof iso === "string" ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return "—";
  const dd = pad(d.getDate());
  const mm = pad(d.getMonth() + 1);
  const yyyy = d.getFullYear();
  return `${pad(d.getHours())}:${pad(d.getMinutes())} · ${dd}/${mm}/${yyyy}`;
}

export function formatTimeVn(iso: string | Date): string {
  const d = typeof iso === "string" ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return "—";
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function formatDateVn(iso: string | Date): string {
  const d = typeof iso === "string" ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return "—";
  return `${DAY_NAMES[d.getDay()]}, ${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
}

export function formatShiftRange(startIso: string, endIso: string): string {
  const start = new Date(startIso);
  const end = new Date(endIso);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return "—";
  const sameDay =
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth() &&
    start.getDate() === end.getDate();
  const h = `${pad(start.getHours())}:${pad(start.getMinutes())} – ${pad(end.getHours())}:${pad(end.getMinutes())}`;
  const date = `${pad(start.getDate())}/${pad(start.getMonth() + 1)}`;
  if (sameDay) return `${h} · ${date}`;
  const endDate = `${pad(end.getDate())}/${pad(end.getMonth() + 1)}`;
  return `${h} · ${date} – ${endDate}`;
}

export function formatRelative(iso: string | Date): string {
  const d = typeof iso === "string" ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return "";
  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.round(diffMs / 60_000);
  if (diffMin < 1) return "Vừa xong";
  if (diffMin < 60) return `${diffMin} phút trước`;
  const diffH = Math.round(diffMin / 60);
  if (diffH < 24) return `${diffH} giờ trước`;
  const diffD = Math.round(diffH / 24);
  if (diffD < 7) return `${diffD} ngày trước`;
  return formatDateVn(d);
}

export function countdownParts(targetIso: string | Date): {
  totalMs: number;
  hours: number;
  minutes: number;
  seconds: number;
  expired: boolean;
} {
  const target =
    typeof targetIso === "string" ? new Date(targetIso) : targetIso;
  const totalMs = target.getTime() - Date.now();
  if (totalMs <= 0) {
    return { totalMs: 0, hours: 0, minutes: 0, seconds: 0, expired: true };
  }
  const totalSec = Math.floor(totalMs / 1000);
  const hours = Math.floor(totalSec / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;
  return { totalMs, hours, minutes, seconds, expired: false };
}
